import React, { useEffect, useMemo } from 'react';
import { Calendar } from 'feather-icons-react/build/IconComponents';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { selectCustomerData } from '../../core/redux/customer/selectors';
import { selectStatuses } from '../../core/redux/status/selectors';
import { selectShipmentFilters } from './store/selectors';
import { actions as shipmentActions, fetchShipmentsWithFilters } from './store/slice';
import { useLocation } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';

const { setCustomer, setStatus, setShipDate, setPageSearchText } = shipmentActions;

const getCustomerId = customer =>
  customer?.id ?? customer?.Id ?? customer?.customerId ?? customer?.CustomerId;

const getCustomerName = customer =>
  String(customer?.customerName ?? customer?.CustomerName ?? '').trim();

const getCustomerShortName = customer =>
  String(customer?.customerShortName ?? customer?.CustomerShortName ?? '').trim();

/** Editable/searchable selects — overrides global input { height: 0 } */
const searchableSelectStyles = {
  control: base => ({
    ...base,
    minHeight: 40,
    cursor: 'text',
  }),
  valueContainer: base => ({
    ...base,
    paddingLeft: 8,
    paddingRight: 8,
  }),
  input: base => ({
    ...base,
    height: 'auto',
    margin: 0,
    padding: 0,
    opacity: 1,
  }),
  singleValue: base => ({
    ...base,
    marginLeft: 0,
  }),
  placeholder: base => ({
    ...base,
    marginLeft: 0,
  }),
  menu: base => ({
    ...base,
    zIndex: 5,
  }),
};

const ShipmentFilter = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const customers = useSelector(selectCustomerData);
  const statuses = useSelector(selectStatuses);
  const {
    customer: selectedCustomer,
    status: selectedStatus,
    shipDate,
  } = useSelector(selectShipmentFilters);

  const isCustomerDashboard = location.pathname === all_routes.shipments;

  const customerOptions = useMemo(() => {
    const list = Array.isArray(customers) ? customers : [];
    return list
      .map(customer => {
        const id = getCustomerId(customer);
        if (id === undefined || id === null || id === '') return null;
        const name = getCustomerName(customer);
        const shortName = getCustomerShortName(customer);
        const searchText = shortName || name;
        const label =
          name && shortName && shortName !== name
            ? `${name} (${shortName})`
            : searchText || String(id);
        return {
          value: String(id),
          label,
          searchText,
          name,
          shortName,
        };
      })
      .filter(Boolean);
  }, [customers]);

  const statusOptions = useMemo(() => {
    const list = Array.isArray(statuses) ? statuses : [];
    return list
      .map(item => {
        const statusName = String(item?.status ?? item?.Status ?? '').trim();
        if (!statusName) return null;
        return {
          value: statusName,
          label: statusName,
        };
      })
      .filter(Boolean);
  }, [statuses]);

  const selectedCustomerOption =
    selectedCustomer &&
    selectedCustomer.value !== undefined &&
    String(selectedCustomer.value) !== '0'
      ? customerOptions.find(o => String(o.value) === String(selectedCustomer.value)) ||
        selectedCustomer
      : null;

  const selectedStatusOption =
    selectedStatus && selectedStatus.value
      ? statusOptions.find(o => String(o.value) === String(selectedStatus.value)) || selectedStatus
      : null;

  useEffect(() => {
    if (isCustomerDashboard && customerOptions.length > 0) {
      const companyId = localStorage.getItem('Company');
      const needsUpdate =
        companyId &&
        (!selectedCustomer || String(selectedCustomer.value) !== String(companyId));

      if (needsUpdate) {
        const customerOption = customerOptions.find(
          option => String(option.value) === String(companyId),
        );
        if (customerOption) {
          dispatch(setCustomer(customerOption));
        }
      }
    }
  }, [dispatch, isCustomerDashboard, customerOptions, selectedCustomer]);

  const handleCustomerChange = selected => {
    const next =
      selected && selected.value !== undefined && String(selected.value) !== '0' ? selected : null;
    dispatch(setCustomer(next));
  };

  const handleStatusChange = selected => {
    dispatch(setStatus(selected && selected.value ? selected : null));
  };

  const filterCustomerOption = (option, rawInput) => {
    const input = String(rawInput || '')
      .trim()
      .toLowerCase();
    if (!input) return true;
    const label = String(option.label || '').toLowerCase();
    const name = String(option.data?.name || '').toLowerCase();
    const shortName = String(option.data?.shortName || '').toLowerCase();
    const searchText = String(option.data?.searchText || '').toLowerCase();
    return (
      label.includes(input) ||
      name.includes(input) ||
      shortName.includes(input) ||
      searchText.includes(input)
    );
  };

  const filterStatusOption = (option, rawInput) => {
    const input = String(rawInput || '')
      .trim()
      .toLowerCase();
    if (!input) return true;
    return String(option.label || '')
      .toLowerCase()
      .includes(input);
  };

  const onSearch = e => {
    e.preventDefault();
    e.stopPropagation();

    const overrides = {
      status: selectedStatusOption,
      shipDate: shipDate || null,
    };

    if (!isCustomerDashboard) {
      const customerSearchText =
        selectedCustomerOption?.searchText ||
        selectedCustomerOption?.shortName ||
        selectedCustomerOption?.name ||
        selectedCustomer?.searchText ||
        selectedCustomer?.shortName ||
        selectedCustomer?.label ||
        '';

      if (
        customerSearchText &&
        String(selectedCustomerOption?.value || selectedCustomer?.value) !== '0'
      ) {
        const text = String(customerSearchText).trim();
        dispatch(setPageSearchText(text));
        overrides.pageSearchText = text;
      }
    }

    dispatch(fetchShipmentsWithFilters(overrides));
  };

  return (
    <div className="card visible" id="shipment_filter_inputs" style={{ display: 'block' }}>
      <div className="card-body pb-0">
        <div className="row">
          <div className="col-lg-4 col-sm-6 col-12">
            <div className="input-blocks">
              <Select
                className="shipment-filter-select"
                classNamePrefix="react-select"
                options={customerOptions}
                value={selectedCustomerOption}
                onChange={handleCustomerChange}
                placeholder="Type to search customer"
                isDisabled={isCustomerDashboard}
                isSearchable
                isClearable
                filterOption={filterCustomerOption}
                styles={searchableSelectStyles}
                noOptionsMessage={() => 'No customers found'}
              />
            </div>
          </div>
          <div className="col-lg-2 col-sm-6 col-12">
            <div className="input-blocks">
              <Select
                className="shipment-filter-select"
                classNamePrefix="react-select"
                options={statusOptions}
                value={selectedStatusOption}
                onChange={handleStatusChange}
                placeholder="Type to search status"
                isSearchable
                isClearable
                filterOption={filterStatusOption}
                styles={searchableSelectStyles}
                noOptionsMessage={() => 'No statuses found'}
              />
            </div>
          </div>
          <div className="col-lg-2 col-sm-6 col-12">
            <div className="input-blocks">
              <Calendar className="info-img" />
              <input
                type="date"
                className="form-control"
                value={shipDate || ''}
                onChange={e => dispatch(setShipDate(e.target.value))}
                placeholder="Ship Date"
              />
            </div>
          </div>

          <div className="col-lg-4 col-sm-6 col-12 ms-auto">
            <div className="input-blocks">
              <button type="button" className="btn btn-filters ms-auto" onClick={onSearch}>
                <i data-feather="search" className="feather-search" /> Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentFilter;
